/**
 * G3 — commerce entitlement deny stays inside the checkout journey shell
 * (checkout / complete / post-kiosk / handoff). Does not Navigate to
 * /unavailable or /recovery/access-denied (no full-page SurfaceUnavailable swap).
 * Backend pay APIs remain authoritative via assertUseCaseEntitled; this is presentation-only.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { JourneyShell } from '../journeys/JourneyShell.js';
import {
  ServerUnavailableScreen,
  TenantInactiveScreen,
  useCustomerSurfaceEntitlement,
} from '../entitlements/customerEntitlementGuards.js';
import { PLATFORM_SHOPS_PATH } from '../shop/resolveShopHref.js';
import { Button } from '../../shared/ui/Button.js';
import { ScreenState } from '../../shared/ui/ScreenState.js';

const COMMERCE_BLOCKS = ['surface_customer', 'product_vending'] as const;

interface CheckoutCommerceEntitlementGateProps {
  readonly children: ReactNode;
}

export function CheckoutCommerceEntitlementGate({
  children,
}: CheckoutCommerceEntitlementGateProps): JSX.Element {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  const { areBlocksEntitled, isLoading, isError, isTenantInactive, refetch } =
    useCustomerSurfaceEntitlement();

  if (isTenantInactive) {
    return <TenantInactiveScreen />;
  }

  if (isError) {
    return <ServerUnavailableScreen onRetry={refetch} />;
  }

  if (isLoading) {
    return (
      <JourneyShell context="PHONE_FIRST_PRODUCT">
        <ScreenState variant="loading" testId="checkout-commerce-entitlement-loading" />
      </JourneyShell>
    );
  }

  if (!areBlocksEntitled(COMMERCE_BLOCKS)) {
    return (
      <JourneyShell
        title={t('entitlement.surface.unavailable.title')}
        context="PHONE_FIRST_PRODUCT"
        backTo={PLATFORM_SHOPS_PATH}
        backLabel={t('nav.shops')}
      >
        <ScreenState
          variant="empty"
          message={t('entitlement.surface.unavailable.body')}
          testId="checkout-commerce-unavailable"
          action={
            <Button
              type="button"
              intent="primary"
              size="md"
              data-testid="checkout-commerce-go-shops"
              onClick={() => {
                navigate(PLATFORM_SHOPS_PATH);
              }}
            >
              {t('nav.shops')}
            </Button>
          }
        />
      </JourneyShell>
    );
  }

  return <>{children}</>;
}
